/**
 * Unity WebGL インスタンスのモジュールスコープのシングルトン管理。
 *
 * ライフサイクル設計（最重要）: シングルトンで保持し、アンマウントでは破棄せず canvas を
 * 画面外の holder へ退避する。Quit() は呼ばない。
 *
 * 理由:
 * - 初期化に 3〜8 秒かかり、Quit() 自体も約1秒かかる。バージョンによってはヒープを
 *   解放しきらない。
 * - VrmaPreview のトグルは自由に往復でき、詳細画面 ⇄ 一覧の遷移も頻繁に起こる。都度
 *   破棄・再生成すると体感が悪い上に、上記コストが毎回かかる。
 * - StrictMode（main.tsx）ではマウント→アンマウント→マウントが起きるが、promise
 *   メモ化シングルトンなら二重初期化しない（Turnstile.tsx の scriptPromise と同じパターン）。
 * - 同一ページに Unity インスタンスが2つ存在する状態は絶対に避けたい（ヒープと WebGL
 *   context が倍になる）。
 *
 * 「この環境で Unity WebGL が使えるか」はセッション限りの実行時の事実であり、
 * previewQuality.ts の永続設定（ユーザーの選択）とは意図的に分離している
 * （getUnityUnavailableReason はページを再読み込みすれば消える）。
 */
import { vrmaHubOnEvent } from '../components/viewer/unityBridge';

export interface UnityInstance {
  SendMessage(gameObject: string, method: string, value?: string | number): void;
  SetFullscreen(v: 0 | 1): void;
  Quit(): Promise<void>;
  /**
   * ★罠（実ブラウザで踏んだ）: `vrmaHubPutBytes` は createUnityInstance に渡した config
   * オブジェクトには生えない。Emscripten の `__postset` が触る内部の `Module` と config は
   * 別オブジェクトのため。Unity 側は生成を `VrmaHub_InstallCanvasHooks()`（`ready` を送る
   * 直前に必ず1回呼ばれる）に移したので、**この `instance.Module` 経由でのみ、かつ `ready`
   * イベント受信後にのみ**存在が保証される。呼び出し側（unityBridge.ts の loadFileAsset）は
   * 必ず `?.` と `typeof === 'function'` の両方でガードし、ready 前に呼び出さないこと。
   */
  Module?: { vrmaHubPutBytes?: (b: ArrayBuffer) => number; canvas?: HTMLCanvasElement };
}

declare global {
  interface Window {
    createUnityInstance?: (
      canvas: HTMLCanvasElement,
      config: object,
      onProgress?: (progress: number) => void,
    ) => Promise<UnityInstance>;
  }
}

export interface UnityHost {
  instance: UnityInstance;
  canvas: HTMLCanvasElement;
  /** canvas を使っていないあいだ退避しておく画面外要素。 */
  holder: HTMLElement;
}

/** CI が S3 の unity-webgl/ prefix に配置するビルド成果物の解決情報。 */
interface UnityBuildManifest {
  schemaVersion: number;
  buildId: string;
  base: string;
  loaderUrl: string;
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streamingAssetsUrl?: string;
  productName?: string;
  commit?: string;
  branch?: string;
  publishedAt?: string;
  publishedBy?: string;
}

const LOAD_TIMEOUT_MS = 30_000;

function getBuildJsonUrl(): string {
  return import.meta.env.VITE_UNITY_BUILD_JSON_URL ?? `${import.meta.env.BASE_URL}unity-webgl/build.json`;
}

/**
 * build.json を fetch して解決する。
 *
 * ★重要な罠: このサイトの CloudFront ディストリビューションは CustomErrorResponses で
 * 403/404 を /index.html の 200 に化けさせる（infra/site.yaml）。Unity が一度もデプロイ
 * されていない環境では、この GET が 200 + HTML を返してしまう。content-type の検査と
 * JSON.parse の try/catch の二重で防御する。
 */
async function fetchBuildManifest(): Promise<UnityBuildManifest> {
  const res = await fetch(getBuildJsonUrl(), { cache: 'no-store' });
  if (!res.ok) throw new Error(`build.json の取得に失敗しました（status ${res.status}）`);
  const contentType = res.headers.get('content-type') ?? '';
  const text = await res.text();
  if (!contentType.includes('json') || text.trim().startsWith('<')) {
    throw new Error('build.json が JSON ではありません（Unity 版が未デプロイの可能性があります）');
  }
  try {
    return JSON.parse(text) as UnityBuildManifest;
  } catch {
    throw new Error('build.json の解析に失敗しました');
  }
}

// Unity ローダー <script> の動的挿入を一度だけ行う（Turnstile.tsx の scriptPromise と同型）。
let loaderScriptSrc: string | null = null;
let loaderScriptPromise: Promise<void> | null = null;

function loadUnityLoaderScript(src: string): Promise<void> {
  if (loaderScriptPromise && loaderScriptSrc === src) return loaderScriptPromise;
  loaderScriptSrc = src;
  loaderScriptPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unity ローダーの読み込みに失敗しました。'));
    document.head.appendChild(script);
  });
  return loaderScriptPromise;
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function createHolder(): HTMLElement {
  const holder = document.createElement('div');
  // display:none ではなく画面外配置にする
  // （display:none は一部ドライバで context loss や 0px リサイズを誘発する）。
  holder.style.cssText =
    'position:fixed;left:-99999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;';
  document.body.appendChild(holder);
  return holder;
}

/** 捨て canvas で getContext('webgl2') を試す。Unity WebGL の前提を満たすかの事前チェック。 */
export function isWebGL2Available(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!canvas.getContext('webgl2');
  } catch {
    return false;
  }
}

// セッション限りの利用不能理由。localStorage には書かない（端末やネットワークが変われば
// 動く可能性があり、ユーザーの設定を勝手に書き換えるのは不親切なため）。
let unavailableReason: string | null = null;

export function markUnityUnavailable(reason: string): void {
  console.warn('[unityViewer] disabled:', reason);
  unavailableReason = reason;
}

export function getUnityUnavailableReason(): string | null {
  return unavailableReason;
}

let hostPromise: Promise<UnityHost> | null = null;
// hostPromise が resolve した後は、attachUnityCanvas/releaseUnity から同期的に
// canvas を出し入れしたいので、resolve 結果もモジュール変数にミラーしておく。
let resolvedHost: UnityHost | null = null;

/**
 * Unity インスタンスを一度だけ生成する。2回目以降の呼び出しは同じ promise を返す
 * （初期化中の重複呼び出しでも二重生成しない）。
 */
export function ensureUnityHost(onProgress?: (progress: number) => void): Promise<UnityHost> {
  if (hostPromise) return hostPromise;

  hostPromise = (async () => {
    if (!isWebGL2Available()) {
      throw new Error('この環境は WebGL2 に対応していないため、リッチ版を表示できません。');
    }

    const manifest = await fetchBuildManifest();
    await loadUnityLoaderScript(manifest.loaderUrl);
    if (!window.createUnityInstance) {
      throw new Error('Unity ローダーの初期化に失敗しました。');
    }

    const holder = createHolder();
    const canvas = document.createElement('canvas');
    // ★必須（実ブラウザで踏んだ）: id が無いと Unity 起動時に落ちる。
    // WebGLInput.captureAllKeyboardInput = false のとき、Unity のランタイムは keydown 等を
    // document ではなく canvas に登録し、そのセレクタを `"#" + canvas.id` で組み立てる。
    // id が空だと `document.querySelector('#')` になり SyntaxError で Module._main が中断、
    // createUnityInstance が reject してリッチ版がまるごと使えなくなる（ライト版へフォールバック）。
    // WebGLTemplates/VrmaHub/index.html（検証用ハーネス）が id を持っていたため、
    // ハーネス経由の検証ではこの経路だけ再現しなかった。id はテンプレートと揃えておく。
    canvas.id = 'vrmahub-canvas';
    canvas.tabIndex = -1; // 誤フォーカスでキー入力を奪わせない
    canvas.style.cssText = 'display:block;width:100%;height:100%;';
    // createUnityInstance の呼び出し前から DOM に居させる（context 確保のため）。
    holder.appendChild(canvas);

    const config = {
      dataUrl: manifest.dataUrl,
      frameworkUrl: manifest.frameworkUrl,
      codeUrl: manifest.codeUrl,
      streamingAssetsUrl: manifest.streamingAssetsUrl,
      companyName: 'VRMA Hub',
      productName: manifest.productName ?? 'VRMA Hub Viewer',
      productVersion: manifest.buildId,
      // out of memory / abort を検出したら以後リッチ版を無効化する（iOS Safari の
      // メモリ上限対策。Quit() は呼ばず、既存インスタンスはそのまま放置する）。
      printErr: (message: string) => {
        console.error('[unity]', message);
        if (/out of memory|abort/i.test(message)) {
          markUnityUnavailable('Unity の初期化中にメモリ不足が発生しました。');
        }
      },
      // Unity → JS のイベント入口。window を汚さず、createUnityInstance の config
      // 経由でインスタンスごとの Module に生やしてもらう。購読者の差し替えは
      // unityBridge.ts の setUnityEventListener が担う。
      //
      // 注意: これは Unity 側が「config に置かれた関数を Module にコピーする」経路で、
      // vrmaHubPutBytes（JS → Unity、Module 側が用意する関数を JS が呼ぶ）とは向きが逆。
      // vrmaHubPutBytes はここに置いても生えないため、config には含めない（UnityInstance
      // 型のコメント、および unityBridge.ts 冒頭のコメント参照）。
      vrmaHubOnEvent,
    };

    const instance = await withTimeout(
      window.createUnityInstance(canvas, config, onProgress),
      LOAD_TIMEOUT_MS,
      'Unity の起動がタイムアウトしました。',
    );

    const host: UnityHost = { instance, canvas, holder };
    resolvedHost = host;
    return host;
  })();

  hostPromise.catch((err) => {
    markUnityUnavailable(err instanceof Error ? err.message : String(err));
    // 失敗時は次回の ensureUnityHost で再試行できるようにリセットする
    // （オフラインだった等、後で状況が変わる可能性があるため）。
    hostPromise = null;
  });

  return hostPromise;
}

let tokenCounter = 0;
// 排他は「最後の取得者が勝つ」方式。0 は「誰も所有していない」を表す。
let currentToken = 0;

/** Unity canvas の利用権を取得する。呼び出しごとに新しいトークンが最新の所有者になる。 */
export function acquireUnity(): number {
  tokenCounter += 1;
  currentToken = tokenCounter;
  return currentToken;
}

/**
 * 渡したトークンが今も最新の所有者かを確認する。
 * ensureUnityHost() は初期化に時間がかかるため、await の完了後に必ずこれを確認してから
 * attach すること（await 中に別のマウントが acquireUnity している可能性がある）。
 */
export function isCurrentOwner(token: number): boolean {
  return token !== 0 && token === currentToken;
}

/** canvas を parent の子として表示する。既に別の所有者に切り替わっていれば何もしない。 */
export function attachUnityCanvas(token: number, parent: HTMLElement): void {
  if (!isCurrentOwner(token) || !resolvedHost) return;
  parent.appendChild(resolvedHost.canvas);
}

/** canvas を画面外の holder へ戻す。Quit() は呼ばない（上記ライフサイクル設計を参照）。 */
export function releaseUnity(token: number): void {
  if (!isCurrentOwner(token)) return; // 既に別の取得者に切り替わっている
  currentToken = 0;
  if (resolvedHost) resolvedHost.holder.appendChild(resolvedHost.canvas);
}
